// src/components/ui/DecryptedText.js
import { useEffect, useRef, useState } from "react";

const defaultCharacters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890<>!?@#$%&*";

export default function DecryptedText({
  text,
  speed = 50,
  maxIterations = 20,
  characters = defaultCharacters,
  animateOn = "load", // "load" | "view" | "hover"
  revealDirection = "left", // "left" | "right" | "center"
  className = "",
  parentClassName = "",
  encryptedClassName = "",
}) {
  const [displayedText, setDisplayedText] = useState("");
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();

  const decrypt = () => {
    if (hasAnimated && animateOn === "view") return;

    let iterations = 0;

    const interval = setInterval(() => {
      iterations++;

      const newText = text
        .split("")
        .map((char, index) => {
          const progress = iterations / maxIterations;

          let shouldReveal = false;

          if (revealDirection === "left") {
            shouldReveal = index < text.length * progress;
          } else if (revealDirection === "right") {
            shouldReveal = index > text.length * (1 - progress);
          } else if (revealDirection === "center") {
            const center = text.length / 2;
            const distance = Math.abs(index - center);
            shouldReveal = distance < center * progress;
          }

          if (shouldReveal) {
            return char;
          }

          return characters[Math.floor(Math.random() * characters.length)];
        })
        .join("");

      setDisplayedText(newText);

      if (iterations >= maxIterations) {
        clearInterval(interval);
        setDisplayedText(text);
        setHasAnimated(true);
      }
    }, speed);
  };

  // Animate on load
  useEffect(() => {
    if (animateOn === "load") decrypt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animate on hover
  const handleMouseEnter = () => {
    if (animateOn === "hover") decrypt();
  };

  // Animate when in view
  useEffect(() => {
    if (animateOn !== "view") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) decrypt();
        });
      },
      { threshold: 0.4 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <span
      ref={ref}
      className={`${parentClassName}`}
      onMouseEnter={handleMouseEnter}
    >
      {displayedText.split("").map((char, index) => (
        <span
          key={index}
          className={`${className} ${
            char === text[index] ? "" : encryptedClassName
          }`}
        >
          {char}
        </span>
      ))}
    </span>
  );
}
